// pages/signature/components/tableEnvelopes/TableEnvelope.tsx
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
	Box,
	Chip,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import React, { useMemo } from "react";
import { globalBackgroundSignature, globalColorTextSignature, globalFormatDates, globalMaskDocument } from "../../../shared/utils";
import { globalTranslateSignatureStatus } from "../../../shared/utils/globalTranslateSignature";
import { IEnvelopeData } from "../types/types";
import SignersCellPro from "./SignersCellPro";

export type EnvelopeTab = "dados" | "signatarios" | "documentos";

const HEADERS = ["Parceiro", "Nome", "Assinaturas", "Criação", "Status", "Ações"] as const;

interface Props {
	rows: IEnvelopeData[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onOpenEnvelope?: (id: string, tab?: EnvelopeTab) => void;
	onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: IEnvelopeData) => void;
}

/** Renderiza o chip de status */
function renderStatusChip(status: string) {
	const label = globalTranslateSignatureStatus(status);
	const bg = globalBackgroundSignature(status);
	const color = globalColorTextSignature(status);
	return (
		<Chip
			size="medium"
			label={label}
			sx={{
				color,
				fontWeight: 500,
				background: bg,
				border: "0.5px solid #f1f1f1",
			}}
		/>
	);
}

export const TableEnvelope: React.FC<Props> = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onOpenEnvelope, onOpenMenu }) => {
	const docsCountById = useMemo(() => {
		const map = new Map<string, number>();
		for (const r of rows) map.set(r.id, r.documents?.length ?? 0);
		return map;
	}, [rows]);

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="Tabela de Envelopes">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{HEADERS.map((header, i) => (
							<TableCell key={header} align={i === HEADERS.length - 1 ? "right" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => {
						const docsCount = docsCountById.get(row.id) ?? 0;
						return (
							<TableRow key={row.id} hover>
								<TableCell>
									<Stack spacing={0.5}>
										<Typography fontWeight="bold">{row.client.name}</Typography>
										<Typography variant="body2" color="text.secondary">
											{globalMaskDocument(row.client.document)}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>
									<Typography>{row.name}</Typography>
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ cursor: "pointer" }}
											onClick={() => onOpenEnvelope?.(row.id, "documentos")}
											title="Ver documentos do envelope"
											aria-label={`Ver documentos do envelope ${row.name}`}
										>
											{docsCount} documentos
										</Typography>
										{docsCount > 0 && (
											<IconButton
												size="small"
												onClick={() => onOpenEnvelope?.(row.id, "documentos")}
												sx={{ ml: 0.5 }}
												aria-label={`Abrir documentos do envelope ${row.name}`}
												title="Ver documentos"
											>
												<DescriptionOutlinedIcon fontSize="small" />
											</IconButton>
										)}
									</Stack>
								</TableCell>
								<TableCell sx={{ minWidth: 200 }}>
									<SignersCellPro
										signatories={row.signatories}
										onOpenDetails={() => onOpenEnvelope?.(row.id, "signatarios")}
										barWidth={160}
										barHeight={7}
										dense
										showTypeBreakdownInTooltip
									/>
								</TableCell>
								<TableCell>
									<Typography>{globalFormatDates(row.createdAt)}</Typography>
									<Typography variant="body2" color="text.secondary">
										{row.createdAt ? format(new Date(row.createdAt), "HH:mm:ss", { locale: ptBR }) : ""}
									</Typography>
								</TableCell>
								<TableCell>{renderStatusChip(row.status)}</TableCell>
								<TableCell align="right">
									<IconButton aria-label={`Ações do envelope ${row.name}`} onClick={(e) => onOpenMenu(e, row)}>
										<MoreVertIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
